import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useCallback, useEffect, useMemo, useState } from 'react';
import useLoadingOverlay from '../../hooks/useLoadingOverlay';
import { getMerchantCodes, getMerchantCodesPageload, saveMerchantCodeBatchByCategory, saveMerchantCodesByCategory } from '../../api/merchantCode';
export const CorpMerchantCodesContext = createContext({
    merchantCodePageload: null,
    merchantCodes: [],
    loading: true,
    error: null,
    filterImportedOnly: false,
    filterGroups: [],
    filterKeyword: '',
    setFilterImportedOnly: () => { },
    setFilterGroups: () => { },
    setFilterKeyword: () => { },
    saveMerchantCode: async () => { },
    saveMerchantCodeBatch: async () => { },
    merchantCodeBeingUpdated: null
});
export default function CorpMerchantCodesProvider({ children }) {
    const { applyLoadingState, removeLoadingState } = useLoadingOverlay();
    const [merchantCodePageload, setMerchantCodePageload] = useState(null);
    const [merchantCodes, setMerchantCodes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filterImportedOnly, setFilterImportedOnly] = useState(false);
    const [filterGroups, setFilterGroups] = useState([]);
    const [filterKeyword, setFilterKeyword] = useState('');
    const [merchantCodeBeingUpdated, setMerchantCodeBeingUpdated] = useState(null);
    const requestMerchantCodesPageload = useCallback(async () => {
        try {
            const encryptedExpCatId = new URLSearchParams(window.location.search).get('e');
            const requestedMerchantCodeData = await getMerchantCodesPageload(encryptedExpCatId);
            setMerchantCodePageload(requestedMerchantCodeData);
        }
        catch (err) {
            setError(err.message);
        }
    }, []);
    const requestMerchantCodes = useCallback(async () => {
        if (!merchantCodePageload?.ExpCatId) {
            return;
        }
        try {
            setError(null);
            const options = {
                ExpCatID: merchantCodePageload.ExpCatId,
                Keyword: filterKeyword,
                ImportedOnly: filterImportedOnly,
                Group: filterGroups.map((group) => group.PK)
            };
            const response = await getMerchantCodes(options);
            const dataGridMerchantCodes = response.map((merchantCode) => ({
                ...merchantCode,
                id: merchantCode.MerchantCodeID
            }));
            setMerchantCodes(dataGridMerchantCodes);
        }
        catch (err) {
            setError(err.message);
        }
    }, [merchantCodePageload?.ExpCatId, filterKeyword, filterImportedOnly, filterGroups]);
    const initializePage = useCallback(async () => {
        try {
            applyLoadingState();
            setError(null);
            setLoading(true);
            await requestMerchantCodesPageload();
        }
        catch (err) {
            setError(err.message);
        }
        finally {
            setLoading(false);
            removeLoadingState();
        }
    }, [requestMerchantCodesPageload, applyLoadingState, removeLoadingState]);
    useEffect(() => {
        (async () => {
            await initializePage();
        })();
    }, [initializePage]);
    useEffect(() => {
        (async () => {
            await requestMerchantCodes();
        })();
    }, [requestMerchantCodes]);
    const saveMerchantCode = useCallback(async (merchantCodeId, selected) => {
        if (!merchantCodePageload) {
            return;
        }
        setMerchantCodeBeingUpdated(merchantCodeId);
        try {
            await saveMerchantCodesByCategory({
                ExpCatID: merchantCodePageload.ExpCatId,
                Selections: [
                    {
                        MerchantCodeID: merchantCodeId,
                        Selected: selected
                    }
                ]
            });
            setMerchantCodePageload((prev) => {
                if (!prev) {
                    return prev;
                }
                return {
                    ...prev,
                    MerchantCodesSelected: selected
                        ? [...prev.MerchantCodesSelected, merchantCodeId]
                        : prev.MerchantCodesSelected.filter((id) => id !== merchantCodeId)
                };
            });
        }
        finally {
            setMerchantCodeBeingUpdated(null);
        }
    }, [merchantCodePageload]);
    const saveMerchantCodeBatch = useCallback(async (merchantCodeIds, selected) => {
        if (!merchantCodePageload) {
            return;
        }
        await saveMerchantCodeBatchByCategory({
            ExpCatID: merchantCodePageload.ExpCatId,
            Search: {
                ExpCatID: merchantCodePageload.ExpCatId,
                Keyword: filterKeyword,
                ImportedOnly: filterImportedOnly,
                Group: filterGroups.map((group) => group.PK)
            },
            Link: selected
        });
        setMerchantCodePageload((prev) => {
            if (!prev) {
                return prev;
            }
            return {
                ...prev,
                MerchantCodesSelected: selected
                    ? [...prev.MerchantCodesSelected, ...merchantCodeIds]
                    : prev.MerchantCodesSelected.filter((id) => !merchantCodeIds.includes(id))
            };
        });
        await requestMerchantCodes();
    }, [
        merchantCodePageload,
        requestMerchantCodes,
        filterKeyword,
        filterImportedOnly,
        filterGroups
    ]);
    const contextValue = useMemo(() => ({
        merchantCodePageload,
        merchantCodes,
        loading,
        error,
        filterImportedOnly,
        filterGroups,
        filterKeyword,
        setFilterImportedOnly,
        setFilterGroups,
        setFilterKeyword,
        saveMerchantCode,
        saveMerchantCodeBatch,
        merchantCodeBeingUpdated
    }), [
        merchantCodePageload,
        merchantCodes,
        loading,
        error,
        filterImportedOnly,
        filterGroups,
        filterKeyword,
        saveMerchantCode,
        saveMerchantCodeBatch,
        merchantCodeBeingUpdated
    ]);
    return (_jsx(CorpMerchantCodesContext.Provider, { value: contextValue, children: children }));
}
